import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader, handleApiError } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit2, Trash2, TrendingUp, TrendingDown, Wallet, PiggyBank, Calendar, Filter, BarChart3, Tag, Smartphone, Wifi, Building2, DollarSign, CreditCard, RefreshCw, Loader2, AlertTriangle, CheckCircle2, Clock, Bell, Settings, X, Send } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';

function formatCurrency(value) { return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0); }
function formatDate(dateStr) { if (!dateStr) return '-'; var p = dateStr.split('-'); return p[2] + '/' + p[1] + '/' + p[0]; }
function getCurrentMonth() { var n = new Date(); return n.getFullYear() + '-' + String(n.getMonth() + 1).padStart(2, '0'); }

export default function FinanceiroPage() {
  const [receitas, setReceitas] = useState([]);
  const [despesas, setDespesas] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [projecao, setProjecao] = useState(null);
  const [filiais, setFiliais] = useState([]);
  const [categorias, setCategorias] = useState({ receitas: [], despesas: [] });
  const [categoriasLoaded, setCategoriasLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resumo');
  const [filterMes, setFilterMes] = useState(getCurrentMonth());
  const [filterFilial, setFilterFilial] = useState('');
  const [filterSituacao, setFilterSituacao] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoriaModalOpen, setCategoriaModalOpen] = useState(false);
  const [modalType, setModalType] = useState('receita');
  const [selectedItem, setSelectedItem] = useState(null);
  const [novaCategoria, setNovaCategoria] = useState('');
  const [faturamentoClientes, setFaturamentoClientes] = useState(null);
  const [editandoMensalidade, setEditandoMensalidade] = useState(false);
  const [lytexCarteira, setLytexCarteira] = useState(null);
  const [lytexLoading, setLytexLoading] = useState(false);
  const [lytexLancando, setLytexLancando] = useState(false);
  const [sincronizandoCustos, setSincronizandoCustos] = useState(false);
  const [alertasDespesas, setAlertasDespesas] = useState({ vencidas: [], vencendo_hoje: [], total_vencido: 0, total_vencendo_hoje: 0 });
  const [configAlertas, setConfigAlertas] = useState(null);
  const [configAlertasOpen, setConfigAlertasOpen] = useState(false);
  const [enviandoTeste, setEnviandoTeste] = useState(false);
  const [formData, setFormData] = useState({
    descricao: '', valor: '', tipo: 'FIXA', categoria: '', filial: '',
    data: new Date().toISOString().split('T')[0], data_vencimento: '', situacao: 'ABERTA', observacoes: '', recorrente: false
  });

  const fetchConfigAlertas = async function() {
    try {
      const res = await axios.get(`${API}/despesas/alertas/config`, { headers: getAuthHeader() });
      setConfigAlertas(res.data);
    } catch (e) { console.error('Erro ao buscar config alertas:', e); }
  };

  const salvarConfigAlertas = async function() {
    try {
      await axios.put(`${API}/despesas/alertas/config`, configAlertas, { headers: getAuthHeader() });
      toast.success('Configuração de alertas salva!');
      setConfigAlertasOpen(false);
    } catch (e) { 
      handleApiError(e);
      toast.error('Erro ao salvar configuração');
    }
  };

  const enviarAlertaTeste = async function() {
    setEnviandoTeste(true);
    try {
      const res = await axios.post(`${API}/despesas/alertas/enviar-teste`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
    } catch (e) { 
      handleApiError(e);
      toast.error(e.response?.data?.detail || 'Erro ao enviar teste');
    } finally {
      setEnviandoTeste(false);
    }
  };

  const adicionarTelefone = function() {
    const tel = prompt('Digite o número do WhatsApp (com DDD):');
    if (tel) {
      setConfigAlertas({
        ...configAlertas,
        telefones_destino: [...(configAlertas.telefones_destino || []), tel]
      });
    }
  };

  const removerTelefone = function(index) {
    const novos = [...(configAlertas.telefones_destino || [])];
    novos.splice(index, 1);
    setConfigAlertas({ ...configAlertas, telefones_destino: novos });
  };

  const salvarMensalidadePlataforma = function(valor) {
    axios.put(API + '/financeiro/config', { mensalidade_plataforma_movel: valor }, { headers: getAuthHeader() })
      .then(function() {
        toast.success('Mensalidade da plataforma atualizada');
        setEditandoMensalidade(false);
        fetchFaturamentoClientes();
      })
      .catch(function(error) {
        handleApiError(error);
        toast.error('Erro ao salvar mensalidade');
      });
  };

  const sincronizarCustosClientes = function() {
    if (!window.confirm('Isso vai atualizar o custo de todos os clientes baseado nos planos cadastrados. Deseja continuar?')) {
      return;
    }
    
    setSincronizandoCustos(true);
    axios.post(API + '/planos/sincronizar-clientes', {}, { headers: getAuthHeader() })
      .then(function(response) {
        toast.success(response.data.message);
        fetchFaturamentoClientes();
      })
      .catch(function(error) {
        handleApiError(error);
        toast.error('Erro ao sincronizar custos');
      })
      .finally(function() {
        setSincronizandoCustos(false);
      });
  };

  useEffect(function() { 
    fetchFiliais(); 
    fetchCategorias().then(function() { setCategoriasLoaded(true); });
    fetchFaturamentoClientes();
    fetchAlertasDespesas();
    fetchConfigAlertas();
  }, []);
  
  useEffect(function() { 
    if (categoriasLoaded) {
      fetchData(); 
    }
  }, [filterMes, filterFilial, filterSituacao, categoriasLoaded]);

  const fetchAlertasDespesas = async function() {
    try {
      const res = await axios.get(`${API}/despesas/alertas`, { headers: getAuthHeader() });
      setAlertasDespesas(res.data);
    } catch (e) { console.error('Erro ao buscar alertas:', e); }
  };

  const atualizarSituacaoDespesa = async function(despesaId, novaSituacao) {
    try {
      await axios.patch(`${API}/despesas/${despesaId}/situacao`, { situacao: novaSituacao }, { headers: getAuthHeader() });
      toast.success(`Despesa marcada como ${novaSituacao}`);
      fetchData();
      fetchAlertasDespesas();
    } catch (e) { 
      handleApiError(e); 
      toast.error('Erro ao atualizar situação');
    }
  };

  const fetchCategorias = async function() {
    try {
      const res = await axios.get(`${API}/financeiro/categorias`, { headers: getAuthHeader() });
      setCategorias(res.data);
      return res.data;
    } catch (e) { handleApiError(e); return { receitas: [], despesas: [] }; }
  };

  const fetchData = async function() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMes) params.append('mes', filterMes);
      if (filterFilial && filterFilial !== 'all') params.append('filial', filterFilial);
      
      // Carregar categorias junto com os outros dados
      const [recRes, despRes, resRes, projRes, catRes] = await Promise.all([
        axios.get(`${API}/receitas?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API}/despesas?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API}/financeiro/resumo?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API}/financeiro/projecao?${filterFilial && filterFilial !== 'all' ? `filial=${filterFilial}` : ''}`, { headers: getAuthHeader() }),
        axios.get(`${API}/financeiro/categorias`, { headers: getAuthHeader() })
      ]);
      setReceitas(recRes.data);
      setDespesas(despRes.data);
      setResumo(resRes.data);
      setProjecao(projRes.data);
      setCategorias(catRes.data);
    } catch (e) { handleApiError(e); }
    setLoading(false);
  };

  const fetchFiliais = async function() {
    try {
      const res = await axios.get(`${API}/filiais`, { headers: getAuthHeader() });
      setFiliais(res.data);
    } catch (e) { handleApiError(e); }
  };

  const fetchFaturamentoClientes = async function() {
    try {
      const res = await axios.get(`${API}/dashboard/stats`, { headers: getAuthHeader() });
      setFaturamentoClientes(res.data);
    } catch (e) { handleApiError(e); }
  };

  const fetchLytexCarteira = async function() {
    setLytexLoading(true);
    try {
      const res = await axios.get(`${API}/lytex/carteira?mes=${filterMes}`, { headers: getAuthHeader() });
      setLytexCarteira(res.data);
    } catch (e) { 
      console.error('Erro ao buscar carteira Lytex:', e);
      setLytexCarteira(null);
    }
    setLytexLoading(false);
  };

  const lancarLytexFinanceiro = async function() {
    setLytexLancando(true);
    try {
      const res = await axios.post(`${API}/lytex/lancar-financeiro`, { 
        mes: filterMes,
        filial: filterFilial || ''
      }, { headers: getAuthHeader() });
      
      if (res.data.success) {
        toast.success('Lançamentos do Lytex realizados com sucesso!');
        fetchData(); // Recarregar dados financeiros
        fetchLytexCarteira(); // Recarregar dados do Lytex
      } else {
        toast.error(res.data.message || 'Nenhuma transação encontrada');
      }
    } catch (e) { 
      handleApiError(e);
      toast.error('Erro ao lançar dados do Lytex');
    }
    setLytexLancando(false);
  };

  const openCreateModal = function(type) {
    setModalType(type);
    setSelectedItem(null);
    setFormData({ descricao: '', valor: '', tipo: 'FIXA', categoria: '', filial: '', data: new Date().toISOString().split('T')[0], data_vencimento: '', situacao: 'ABERTA', observacoes: '', recorrente: false });
    setModalOpen(true);
  };

  const openEditModal = function(item, type) {
    setModalType(type);
    setSelectedItem(item);
    // Formatar valor para exibição no formato brasileiro
    const valorFormatado = item.valor ? Number(item.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
    setFormData({ descricao: item.descricao || '', valor: valorFormatado, tipo: item.tipo || 'FIXA', categoria: item.categoria || '', filial: item.filial || '', data: item.data || '', data_vencimento: item.data_vencimento || '', situacao: item.situacao || 'ABERTA', observacoes: item.observacoes || '', recorrente: item.recorrente || false });
    setModalOpen(true);
  };

  // Função para converter valor em formato brasileiro (3.700,00) para número
  const parseValorBR = function(valor) {
    if (!valor) return 0;
    if (typeof valor === 'number') return valor;
    // Remove pontos (milhares) e substitui vírgula por ponto (decimal)
    const valorLimpo = valor.toString().replace(/\./g, '').replace(',', '.');
    return parseFloat(valorLimpo) || 0;
  };

  // Função para formatar valor para exibição no input (formato brasileiro)
  const formatValorInput = function(valor) {
    if (!valor && valor !== 0) return '';
    const num = typeof valor === 'string' ? parseValorBR(valor) : valor;
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Handler para input de valor monetário
  const handleValorChange = function(e) {
    // Permite apenas números, vírgula e ponto
    let valor = e.target.value.replace(/[^\d.,]/g, '');
    setFormData({...formData, valor: valor});
  };

  const handleSubmit = async function(e) {
    e.preventDefault();
    const data = { 
      descricao: formData.descricao, 
      valor: parseValorBR(formData.valor), 
      tipo: formData.tipo, 
      categoria: formData.categoria, 
      filial: formData.filial || null, 
      data: formData.data, 
      observacoes: formData.observacoes || null, 
      recorrente: formData.recorrente 
    };
    
    // Adicionar campos extras para despesas
    if (modalType === 'despesa') {
      data.data_vencimento = formData.data_vencimento || null;
      data.situacao = formData.situacao || 'ABERTA';
    }
    
    const endpoint = modalType === 'receita' ? '/receitas' : '/despesas';
    try {
      if (selectedItem) await axios.put(`${API}${endpoint}/${selectedItem.id}`, data, { headers: getAuthHeader() });
      else await axios.post(`${API}${endpoint}`, data, { headers: getAuthHeader() });
      toast.success(selectedItem ? 'Atualizado!' : 'Criado!');
      setModalOpen(false);
      fetchData();
      if (modalType === 'despesa') fetchAlertasDespesas();
    } catch (e) { handleApiError(e); toast.error('Erro ao salvar'); }
  };

  const handleDelete = async function() {
    const endpoint = modalType === 'receita' ? '/receitas' : '/despesas';
    try {
      await axios.delete(`${API}${endpoint}/${selectedItem.id}`, { headers: getAuthHeader() });
      toast.success('Excluído!');
      setDeleteModalOpen(false);
      fetchData();
    } catch (e) { handleApiError(e); toast.error('Erro ao excluir'); }
  };

  const handleCreateCategoria = async function() {
    if (!novaCategoria.trim()) {
      toast.error('Digite o nome da categoria');
      return;
    }
    try {
      await axios.post(`${API}/financeiro/categorias`, {
        label: novaCategoria.trim(),
        tipo: modalType
      }, { headers: getAuthHeader() });
      toast.success('Categoria criada!');
      setNovaCategoria('');
      fetchCategorias();
    } catch (e) { 
      handleApiError(e); 
      toast.error('Erro ao criar categoria'); 
    }
  };

  const handleDeleteCategoria = async function(categoriaValue) {
    if (!window.confirm('Excluir esta categoria?')) return;
    try {
      await axios.delete(`${API}/financeiro/categorias/${categoriaValue}?tipo=${modalType}`, { headers: getAuthHeader() });
      toast.success('Categoria excluída!');
      fetchCategorias();
    } catch (e) { 
      if (e.response?.data?.detail) {
        toast.error(e.response.data.detail);
      } else {
        handleApiError(e); 
        toast.error('Erro ao excluir categoria');
      }
    }
  };

  const getCatLabel = function(val, type) {
    const cats = type === 'receita' ? categorias.receitas : categorias.despesas;
    return cats.find(function(c) { return c.value === val; })?.label || val;
  };

  // Transforma os dados do gráfico para mostrar o label das categorias
  const getChartDataWithLabels = function(data, type) {
    if (!data) return [];
    return data.map(function(item) {
      return {
        categoria: getCatLabel(item.categoria, type),
        total: item.total
      };
    });
  };

  const currentCategorias = modalType === 'receita' ? categorias.receitas : categorias.despesas;

  // Carregar dados do Lytex quando o mês mudar
  useEffect(function() {
    fetchLytexCarteira();
  }, [filterMes]);

  const CustomTooltip = function(props) {
    var active = props.active;
    var payload = props.payload;
    var label = props.label;
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
          <p className="text-slate-600 text-sm font-semibold mb-2">{label}</p>
          {payload.map(function(item, i) { return <p key={i} style={{ color: item.color }} className="text-sm">{item.name}: {formatCurrency(item.value)}</p>; })}
        </div>
      );
    }
    return null;;
  };

  return (
    <div className="space-y-6" data-testid="financeiro-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">GESTÃO FINANCEIRA</h1>
          <p className="text-slate-500">Controle de receitas e despesas</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => openCreateModal('receita')} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Plus className="w-4 h-4 mr-2" />Nova Receita</Button>
          <Button onClick={() => openCreateModal('despesa')} className="bg-red-500 hover:bg-red-600 text-white"><Plus className="w-4 h-4 mr-2" />Nova Despesa</Button>
        </div>
      </div>

      <Card className="bg-white border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <Label className="text-slate-600 text-sm mb-2 block">Mês</Label>
              <Input type="month" value={filterMes} onChange={(e) => setFilterMes(e.target.value)} className="bg-white border-slate-300" />
            </div>
            <div className="flex-1">
              <Label className="text-slate-600 text-sm mb-2 block">Filial</Label>
              <Select value={filterFilial} onValueChange={setFilterFilial}>
                <SelectTrigger className="bg-white border-slate-300"><SelectValue placeholder="Todas as filiais" /></SelectTrigger>
                <SelectContent className="bg-white z-[100]">
                  <SelectItem value="all">Todas as filiais</SelectItem>
                  <SelectItem value="Passa Quatro">🔵 Passa Quatro (P4)</SelectItem>
                  <SelectItem value="São Paulo">🟢 São Paulo (SP)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={fetchData} className="bg-slate-800 hover:bg-slate-700 text-white"><Filter className="w-4 h-4 mr-2" />Filtrar</Button>
            <Button 
              onClick={() => {
                if (window.confirm(`Deseja inicializar o mês ${filterMes}?\n\nIsso irá:\n- Replicar despesas FIXAS (nome e valor) do mês anterior\n- Replicar despesas VARIÁVEIS (apenas nome) do mês anterior\n- Replicar receitas FIXAS (nome e valor) do mês anterior\n- Buscar receitas Lytex automaticamente`)) {
                  axios.post(API + '/financeiro/inicializar-mes?mes=' + filterMes, {}, { headers: getAuthHeader() })
                    .then(res => {
                      toast.success(`Mês inicializado! Despesas fixas: ${res.data.despesas_fixas_replicadas}, Variáveis: ${res.data.despesas_variaveis_replicadas}, Receitas fixas: ${res.data.receitas_fixas_replicadas}, Lytex: ${res.data.receitas_lytex_criadas}`);
                      fetchData();
                    })
                    .catch(err => toast.error('Erro ao inicializar mês: ' + (err.response?.data?.detail || err.message)));
                }
              }} 
              variant="outline"
              className="border-orange-500 text-orange-600 hover:bg-orange-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />Inicializar Mês
            </Button>
          </div>
        </CardContent>
      </Card>

      {resumo && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm mb-1">Receita Total</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(resumo.receitas.total)}</p>
                  <p className="text-xs text-slate-400 mt-1">Fixas: {formatCurrency(resumo.receitas.fixas)} | Var: {formatCurrency(resumo.receitas.variaveis)}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center"><TrendingUp className="w-6 h-6 text-white" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm mb-1">Despesa Total</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(resumo.despesas.total)}</p>
                  <p className="text-xs text-slate-400 mt-1">Fixas: {formatCurrency(resumo.despesas.fixas)} | Var: {formatCurrency(resumo.despesas.variaveis)}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-red-500 flex items-center justify-center"><TrendingDown className="w-6 h-6 text-white" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm mb-1">Saldo do Mês</p>
                  <p className={`text-2xl font-bold ${resumo.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(resumo.saldo)}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${resumo.saldo >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}><Wallet className="w-6 h-6 text-white" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm mb-1">Saldo Projetado/Mês</p>
                  <p className={`text-2xl font-bold ${(projecao?.saldo_mensal_projetado || 0) >= 0 ? 'text-slate-800' : 'text-red-600'}`}>{formatCurrency(projecao?.saldo_mensal_projetado || 0)}</p>
                  <p className="text-xs text-slate-400 mt-1">Base: recorrentes</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center"><PiggyBank className="w-6 h-6 text-white" /></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* TabsList com scroll horizontal no mobile */}
        <div className="w-full overflow-x-auto pb-2 -mx-1 px-1">
          <TabsList className="bg-slate-800 p-2 sm:p-3 rounded-xl shadow-lg inline-flex min-w-max gap-1 sm:gap-2">
            <TabsTrigger value="resumo" className="text-slate-300 font-semibold text-xs sm:text-base px-3 sm:px-6 py-2 sm:py-3 rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:text-white hover:bg-slate-700 transition-all whitespace-nowrap">Resumo</TabsTrigger>
            <TabsTrigger value="faturamento" className="text-slate-300 font-semibold text-xs sm:text-base px-3 sm:px-6 py-2 sm:py-3 rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:text-white hover:bg-slate-700 transition-all whitespace-nowrap">Faturamento</TabsTrigger>
            <TabsTrigger value="receitas" className="text-slate-300 font-semibold text-xs sm:text-base px-3 sm:px-6 py-2 sm:py-3 rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:text-white hover:bg-slate-700 transition-all whitespace-nowrap">Receitas</TabsTrigger>
            <TabsTrigger value="despesas" className="text-slate-300 font-semibold text-xs sm:text-base px-3 sm:px-6 py-2 sm:py-3 rounded-lg data-[state=active]:bg-red-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:text-white hover:bg-slate-700 transition-all whitespace-nowrap">Despesas</TabsTrigger>
            <TabsTrigger value="projecao" className="text-slate-300 font-semibold text-xs sm:text-base px-3 sm:px-6 py-2 sm:py-3 rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:text-white hover:bg-slate-700 transition-all whitespace-nowrap">Projeção</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="resumo" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-slate-800 flex items-center gap-2"><BarChart3 className="w-5 h-5" />Receitas por Categoria</CardTitle></CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={resumo?.receitas.por_categoria || []} margin={{ top: 10, right: 10, left: 10, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis 
                        dataKey="categoria" 
                        stroke="#64748B" 
                        fontSize={10} 
                        interval={0} 
                        angle={-45} 
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis stroke="#64748B" fontSize={11} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="total" name="Total" fill="#059669" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-slate-800 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-red-500" />Despesas por Categoria</CardTitle></CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={resumo?.despesas.por_categoria || []} margin={{ top: 10, right: 10, left: 10, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis 
                        dataKey="categoria" 
                        stroke="#64748B" 
                        fontSize={10} 
                        interval={0} 
                        angle={-45} 
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis stroke="#64748B" fontSize={11} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="total" name="Total" fill="#DC2626" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Card Lytex Pay */}
          <Card className="bg-gradient-to-r from-violet-600 to-purple-600 border-0 shadow-lg mt-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Lytex Pay - Carteira
                <Button 
                  onClick={fetchLytexCarteira} 
                  variant="ghost" 
                  size="sm" 
                  className="ml-auto text-white hover:bg-white/20"
                  disabled={lytexLoading}
                >
                  {lytexLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lytexCarteira ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                    <div className="bg-white/10 rounded-lg p-2 sm:p-3">
                      <p className="text-white/70 text-xs">Saldo Atual</p>
                      <p className="text-white text-sm sm:text-lg md:text-xl font-bold truncate">{formatCurrency(lytexCarteira.saldo_atual)}</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2 sm:p-3">
                      <p className="text-white/70 text-xs leading-tight">Entradas Brutas</p>
                      <p className="text-emerald-300 text-sm sm:text-lg md:text-xl font-bold truncate">{formatCurrency(lytexCarteira.resumo?.total_bruto || 0)}</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2 sm:p-3">
                      <p className="text-white/70 text-xs leading-tight">Taxas Lytex</p>
                      <p className="text-red-300 text-sm sm:text-lg md:text-xl font-bold truncate">{formatCurrency(lytexCarteira.resumo?.total_taxas || 0)}</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2 sm:p-3">
                      <p className="text-white/70 text-xs">Líquido</p>
                      <p className="text-white text-sm sm:text-lg md:text-xl font-bold truncate">{formatCurrency(lytexCarteira.resumo?.total_liquido || 0)}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-white/20">
                    <p className="text-white/70 text-sm">
                      {lytexCarteira.resumo?.quantidade_transacoes || 0} transações no período
                    </p>
                    <Button 
                      onClick={lancarLytexFinanceiro} 
                      disabled={lytexLancando || (lytexCarteira.resumo?.total_bruto === 0)}
                      className="bg-white text-purple-600 hover:bg-white/90"
                    >
                      {lytexLancando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                      Lançar no Financeiro
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-white/70">
                  {lytexLoading ? 'Carregando dados da Lytex...' : 'Clique em atualizar para carregar os dados'}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABA FATURAMENTO CLIENTES - Receita, Custo, Lucro dos Clientes */}
        <TabsContent value="faturamento" className="mt-4">
          {/* Botão Sincronizar Custos */}
          <div className="flex justify-end mb-4">
            <Button 
              onClick={sincronizarCustosClientes} 
              disabled={sincronizandoCustos}
              variant="outline"
              size="sm"
              className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
              title="Atualiza o custo de todos os clientes baseado nos planos cadastrados"
            >
              {sincronizandoCustos ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sincronizar Custos dos Planos
            </Button>
          </div>
          
          {/* Cards de Totais */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-500 text-xs sm:text-sm mb-1">Receita Clientes</p>
                    <p className="text-lg sm:text-2xl font-bold text-emerald-600 truncate">{formatCurrency(faturamentoClientes?.receita_total)}</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0 ml-2"><TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" /></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-500 text-xs sm:text-sm mb-1">Custo Total</p>
                    <p className="text-lg sm:text-2xl font-bold text-orange-600 truncate">{formatCurrency(faturamentoClientes?.custo_total)}</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0 ml-2"><TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-white" /></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-500 text-xs sm:text-sm mb-1">Lucro Real</p>
                    <p className="text-lg sm:text-2xl font-bold text-blue-600 truncate">{formatCurrency(faturamentoClientes?.lucro_total)}</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0 ml-2"><DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-white" /></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-500 text-xs sm:text-sm mb-1">Custo Sócios</p>
                    <p className="text-lg sm:text-2xl font-bold text-purple-600 truncate">{formatCurrency(faturamentoClientes?.custo_socios)}</p>
                    <p className="text-xs text-slate-400 mt-1">{faturamentoClientes?.clientes_socios || 0} clientes</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-500 flex items-center justify-center flex-shrink-0 ml-2"><Wallet className="w-5 h-5 sm:w-6 sm:h-6 text-white" /></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Receita e Lucro por Filial */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-800 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-600" />
                  RECEITA E LUCRO POR FILIAL
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(faturamentoClientes?.financeiro_por_filial || []).map(function(filial, index) {
                    return (
                      <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-slate-700 font-semibold">{filial.filial}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-slate-400">Receita</p>
                            <p className="text-emerald-600 font-semibold">{formatCurrency(filial.receita)}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Custo</p>
                            <p className="text-orange-600 font-semibold">{formatCurrency(filial.custo)}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Lucro</p>
                            <p className={filial.lucro >= 0 ? 'font-semibold text-blue-600' : 'font-semibold text-red-600'}>
                              {formatCurrency(filial.lucro)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Gráfico de Barras */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-800 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Receita vs Lucro por Filial
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={faturamentoClientes?.financeiro_por_filial || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" stroke="#64748B" fontSize={11} tickFormatter={(v) => formatCurrency(v)} />
                      <YAxis type="category" dataKey="filial" stroke="#64748B" fontSize={11} width={150} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="receita" name="Receita" fill="#059669" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="lucro" name="Lucro" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Móvel por Filial */}
          <Card className="bg-white border-slate-200 shadow-sm mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-800 flex items-center gap-2 text-base sm:text-lg">
                <Smartphone className="w-5 h-5 text-blue-600" />
                CLIENTES MÓVEL POR FILIAL
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-stretch">
                <div className="space-y-3 flex flex-col justify-center">
                  {(faturamentoClientes?.movel_por_filial || []).map(function(filial, index) {
                    return (
                      <div key={index} className="bg-blue-50 rounded-lg p-3 sm:p-4 border border-blue-200">
                        <div className="flex items-center justify-between mb-2 sm:mb-3">
                          <span className="text-blue-700 font-semibold flex items-center gap-2 text-sm sm:text-base">
                            <Smartphone className="w-4 h-4 text-blue-600" />
                            {filial.filial}
                          </span>
                          <span className="text-blue-600 text-xs sm:text-sm font-bold">{filial.quantidade} clientes</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm">
                          <div>
                            <p className="text-blue-400">Receita</p>
                            <p className="text-blue-800 font-semibold truncate">{formatCurrency(filial.receita)}</p>
                          </div>
                          <div>
                            <p className="text-blue-400">Custo</p>
                            <p className="text-orange-600 font-semibold truncate">{formatCurrency(filial.custo)}</p>
                          </div>
                          <div>
                            <p className="text-blue-400">Lucro</p>
                            <p className={filial.lucro >= 0 ? 'font-semibold text-emerald-600 truncate' : 'font-semibold text-red-600 truncate'}>
                              {formatCurrency(filial.lucro)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Linha de Mensalidade Plataforma - Custo Fixo Editável */}
                  <div className="bg-orange-50 rounded-lg p-3 sm:p-4 border border-orange-300">
                    <div className="flex items-center justify-between mb-2 sm:mb-3 flex-wrap gap-2">
                      <span className="text-orange-700 font-semibold flex items-center gap-2 text-sm sm:text-base">
                        <DollarSign className="w-4 h-4 text-orange-600" />
                        Mensalidade Plataforma
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-orange-600 hover:text-orange-800 hover:bg-orange-100 h-7 px-2"
                        onClick={function() { setEditandoMensalidade(true); }}
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm">
                      <div>
                        <p className="text-orange-400">Receita</p>
                        <p className="text-orange-800 font-semibold">-</p>
                      </div>
                      <div>
                        <p className="text-orange-400">Custo</p>
                        {editandoMensalidade ? (
                          <div className="flex items-center gap-1">
                            <span className="text-orange-600">R$</span>
                            <Input
                              type="number"
                              step="0.01"
                              defaultValue={faturamentoClientes?.config_custos_fixos?.mensalidade_plataforma_movel || 395}
                              className="w-20 h-7 text-sm bg-white border-orange-300"
                              onBlur={function(e) {
                                salvarMensalidadePlataforma(parseFloat(e.target.value) || 395);
                              }}
                              onKeyDown={function(e) {
                                if (e.key === 'Enter') {
                                  salvarMensalidadePlataforma(parseFloat(e.target.value) || 395);
                                }
                              }}
                              autoFocus
                            />
                          </div>
                        ) : (
                          <p className="text-orange-600 font-bold">{formatCurrency(faturamentoClientes?.config_custos_fixos?.mensalidade_plataforma_movel || 395)}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-orange-400">Lucro</p>
                        <p className="font-semibold text-red-600">{formatCurrency(-(faturamentoClientes?.config_custos_fixos?.mensalidade_plataforma_movel || 395))}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-4 sm:p-6 text-white shadow-lg justify-center">
                  <div className="text-center border-b border-white/20 pb-3 sm:pb-4 mb-3 sm:mb-4">
                    <Smartphone className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 text-white" />
                    <p className="text-white/80 text-xs uppercase tracking-wider font-semibold">Total Clientes Móvel</p>
                    <p className="text-3xl sm:text-5xl font-bold mt-1">{faturamentoClientes?.clientes_movel || 0}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center mb-3 sm:mb-4">
                    <div className="bg-white/10 rounded-lg p-2 sm:p-3">
                      <p className="text-white/70 text-xs font-medium">Receita</p>
                      <p className="font-bold text-xs sm:text-lg text-white truncate">{formatCurrency((faturamentoClientes?.movel_por_filial || []).reduce((a,b) => a + (b.receita || 0), 0))}</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2 sm:p-3">
                      <p className="text-white/70 text-xs font-medium">Custo</p>
                      <p className="font-bold text-xs sm:text-lg text-white truncate">{formatCurrency((faturamentoClientes?.movel_por_filial || []).reduce((a,b) => a + (b.custo || 0), 0) + (faturamentoClientes?.config_custos_fixos?.mensalidade_plataforma_movel || 395))}</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2 sm:p-3">
                      <p className="text-white/70 text-xs font-medium">Lucro</p>
                      <p className="font-bold text-xs sm:text-lg text-emerald-300 truncate">{formatCurrency((faturamentoClientes?.movel_por_filial || []).reduce((a,b) => a + (b.lucro || 0), 0) - (faturamentoClientes?.config_custos_fixos?.mensalidade_plataforma_movel || 395))}</p>
                    </div>
                  </div>
                  
                  {/* Seção Clientes Ativos */}
                  <div className="bg-emerald-500 rounded-lg p-3 sm:p-4 text-center">
                    <p className="text-white text-xs uppercase tracking-wider font-bold mb-1">Clientes Ativos</p>
                    <p className="text-2xl sm:text-4xl font-bold text-white">{faturamentoClientes?.movel_ativos?.quantidade || 0}</p>
                    <div className="grid grid-cols-3 gap-1 sm:gap-2 mt-2 sm:mt-3">
                      <div className="bg-white/20 rounded p-1 sm:p-2">
                        <p className="text-white/80 text-xs">Receita</p>
                        <p className="font-bold text-xs sm:text-sm text-white truncate">{formatCurrency(faturamentoClientes?.movel_ativos?.receita || 0)}</p>
                      </div>
                      <div className="bg-white/20 rounded p-1 sm:p-2">
                        <p className="text-white/80 text-xs">Custo</p>
                        <p className="font-bold text-xs sm:text-sm text-white truncate">{formatCurrency((faturamentoClientes?.movel_ativos?.custo || 0) + (faturamentoClientes?.config_custos_fixos?.mensalidade_plataforma_movel || 395))}</p>
                      </div>
                      <div className="bg-white/20 rounded p-1 sm:p-2">
                        <p className="text-white/80 text-xs">Lucro</p>
                        <p className="font-bold text-xs sm:text-sm text-yellow-300 truncate">{formatCurrency((faturamentoClientes?.movel_ativos?.lucro || 0) - (faturamentoClientes?.config_custos_fixos?.mensalidade_plataforma_movel || 395))}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fibra por Filial */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-800 flex items-center gap-2">
                <Wifi className="w-5 h-5 text-emerald-600" />
                CLIENTES FIBRA POR FILIAL
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  {(faturamentoClientes?.fibra_por_filial || []).map(function(filial, index) {
                    return (
                      <div key={index} className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-emerald-700 font-semibold flex items-center gap-2">
                            <Wifi className="w-4 h-4 text-emerald-600" />
                            {filial.filial}
                          </span>
                          <span className="text-emerald-600 text-sm font-bold">{filial.quantidade} clientes</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-emerald-400">Receita</p>
                            <p className="text-emerald-800 font-semibold">{formatCurrency(filial.receita)}</p>
                          </div>
                          <div>
                            <p className="text-emerald-400">Custo</p>
                            <p className="text-orange-600 font-semibold">{formatCurrency(filial.custo)}</p>
                          </div>
                          <div>
                            <p className="text-emerald-400">Lucro</p>
                            <p className={filial.lucro >= 0 ? 'font-semibold text-blue-600' : 'font-semibold text-red-600'}>
                              {formatCurrency(filial.lucro)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-col justify-center items-center bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl p-4 sm:p-6 text-white">
                  <Wifi className="w-8 h-8 sm:w-12 sm:h-12 mb-2 sm:mb-4 opacity-80" />
                  <p className="text-emerald-100 text-xs sm:text-sm uppercase tracking-wider text-center">Total Clientes Fibra</p>
                  <p className="text-2xl sm:text-4xl font-bold mt-1 sm:mt-2">{faturamentoClientes?.clientes_fibra || 0}</p>
                  <div className="mt-3 sm:mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-6 text-center w-full">
                    <div className="flex sm:block justify-between items-center sm:items-start bg-emerald-500/30 sm:bg-transparent rounded p-2 sm:p-0">
                      <p className="text-emerald-200 text-xs">Receita</p>
                      <p className="font-semibold text-sm sm:text-base">{formatCurrency((faturamentoClientes?.fibra_por_filial || []).reduce((a,b) => a + (b.receita || 0), 0))}</p>
                    </div>
                    <div className="flex sm:block justify-between items-center sm:items-start bg-emerald-500/30 sm:bg-transparent rounded p-2 sm:p-0">
                      <p className="text-emerald-200 text-xs">Custo</p>
                      <p className="font-semibold text-sm sm:text-base">{formatCurrency((faturamentoClientes?.fibra_por_filial || []).reduce((a,b) => a + (b.custo || 0), 0))}</p>
                    </div>
                    <div className="flex sm:block justify-between items-center sm:items-start bg-emerald-500/30 sm:bg-transparent rounded p-2 sm:p-0">
                      <p className="text-emerald-200 text-xs">Lucro</p>
                      <p className="font-semibold text-sm sm:text-base">{formatCurrency((faturamentoClientes?.fibra_por_filial || []).reduce((a,b) => a + (b.lucro || 0), 0))}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="receitas" className="mt-4">
          <Card className="bg-white border-slate-200 shadow-sm overflow-hidden">
            <div className="max-h-[500px] overflow-y-auto relative">
              <table className="w-full caption-bottom text-sm">
                <thead className="sticky top-0 z-20 bg-emerald-600">
                  <tr className="border-b">
                    <th className="h-10 px-3 text-left align-middle font-bold text-white">Data</th>
                    <th className="h-10 px-3 text-left align-middle font-bold text-white">Descrição</th>
                    <th className="h-10 px-3 text-left align-middle font-bold text-white">Categoria</th>
                    <th className="h-10 px-3 text-left align-middle font-bold text-white">Tipo</th>
                    <th className="h-10 px-3 text-left align-middle font-bold text-white">Filial</th>
                    <th className="h-10 px-3 text-left align-middle font-bold text-white">Valor</th>
                    <th className="h-10 px-3 text-right align-middle font-bold text-white">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {receitas.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-10 text-slate-400">Nenhuma receita</td></tr>
                  ) : receitas.map((item, i) => (
                    <tr key={item.id} className={`border-b ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                      <td className="p-2 align-middle">{formatDate(item.data)}</td>
                      <td className="p-2 align-middle font-medium">{item.descricao}{item.recorrente && <span className="ml-2 text-xs bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full">Recorrente</span>}</td>
                      <td className="p-2 align-middle"><span className="bg-emerald-100 text-emerald-600 text-xs px-2 py-1 rounded-full">{getCatLabel(item.categoria, 'receita')}</span></td>
                      <td className="p-2 align-middle"><span className={`text-xs px-2 py-1 rounded-full ${item.tipo === 'FIXA' ? 'bg-slate-100 text-slate-600' : 'bg-violet-100 text-violet-600'}`}>{item.tipo === 'FIXA' ? 'Fixa' : 'Variável'}</span></td>
                      <td className="p-2 align-middle">{item.filial || '-'}</td>
                      <td className="p-2 align-middle text-emerald-600 font-bold">{formatCurrency(item.valor)}</td>
                      <td className="p-2 align-middle text-right">
                        <Button onClick={() => openEditModal(item, 'receita')} variant="ghost" size="sm"><Edit2 className="w-4 h-4" /></Button>
                        <Button onClick={() => { setSelectedItem(item); setModalType('receita'); setDeleteModalOpen(true); }} variant="ghost" size="sm" className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="despesas" className="mt-4">
          {/* Alertas de Despesas Vencidas */}
          {(alertasDespesas.quantidade_vencidas > 0 || alertasDespesas.quantidade_vencendo > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {alertasDespesas.quantidade_vencidas > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <div className="bg-red-500 text-white p-2 rounded-full">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-red-700">{alertasDespesas.quantidade_vencidas} despesa(s) vencida(s)</p>
                    <p className="text-red-600 text-sm">Total: {formatCurrency(alertasDespesas.total_vencido)}</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-red-300 text-red-600 hover:bg-red-100"
                    onClick={() => setFilterSituacao('VENCIDA')}
                  >
                    Ver
                  </Button>
                </div>
              )}
              {alertasDespesas.quantidade_vencendo > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
                  <div className="bg-amber-500 text-white p-2 rounded-full">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-amber-700">{alertasDespesas.quantidade_vencendo} despesa(s) vencendo hoje</p>
                    <p className="text-amber-600 text-sm">Total: {formatCurrency(alertasDespesas.total_vencendo_hoje)}</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-amber-300 text-amber-600 hover:bg-amber-100"
                    onClick={() => setFilterSituacao('HOJE')}
                  >
                    Ver
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* Filtro por Situação e Botão de Configurar Alertas */}
          <div className="flex gap-2 mb-4 flex-wrap items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              <Button 
                size="sm" 
                variant={filterSituacao === '' ? 'default' : 'outline'}
                onClick={() => setFilterSituacao('')}
              >
                Todas
              </Button>
              <Button 
                size="sm" 
                variant={filterSituacao === 'ABERTA' ? 'default' : 'outline'}
                className={filterSituacao === 'ABERTA' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                onClick={() => setFilterSituacao('ABERTA')}
              >
                <Clock className="w-4 h-4 mr-1" /> Abertas
              </Button>
              <Button 
                size="sm" 
                variant={filterSituacao === 'PAGA' ? 'default' : 'outline'}
                className={filterSituacao === 'PAGA' ? 'bg-green-500 hover:bg-green-600' : ''}
                onClick={() => setFilterSituacao('PAGA')}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" /> Pagas
              </Button>
            </div>
            
            {/* Botão de Configurar Alertas WhatsApp */}
            <Button 
              size="sm" 
              variant="outline"
              className="border-blue-300 text-blue-600 hover:bg-blue-50"
              onClick={() => setConfigAlertasOpen(true)}
            >
              <Bell className="w-4 h-4 mr-1" /> Alertas WhatsApp
            </Button>
          </div>
          
          <Card className="bg-white border-slate-200 shadow-sm overflow-hidden">
            <div className="max-h-[500px] overflow-y-auto relative">
              <table className="w-full caption-bottom text-sm">
                <thead className="sticky top-0 z-20 bg-red-500">
                  <tr className="border-b">
                    <th className="h-10 px-3 text-left align-middle font-bold text-white">Vencimento</th>
                    <th className="h-10 px-3 text-left align-middle font-bold text-white">Descrição</th>
                    <th className="h-10 px-3 text-left align-middle font-bold text-white">Categoria</th>
                    <th className="h-10 px-3 text-left align-middle font-bold text-white">Situação</th>
                    <th className="h-10 px-3 text-left align-middle font-bold text-white">Filial</th>
                    <th className="h-10 px-3 text-left align-middle font-bold text-white">Valor</th>
                    <th className="h-10 px-3 text-right align-middle font-bold text-white">Ações</th>
                  </tr>
                </thead>
                <tbody>
                {despesas.filter(d => {
                  if (!filterSituacao) return true;
                  if (filterSituacao === 'VENCIDA') {
                    const hoje = new Date().toISOString().split('T')[0];
                    return (d.situacao === 'ABERTA' || !d.situacao) && d.data_vencimento && d.data_vencimento < hoje;
                  }
                  if (filterSituacao === 'HOJE') {
                    const hoje = new Date().toISOString().split('T')[0];
                    return (d.situacao === 'ABERTA' || !d.situacao) && d.data_vencimento === hoje;
                  }
                  return d.situacao === filterSituacao || (!d.situacao && filterSituacao === 'ABERTA');
                }).length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-10 text-slate-400">Nenhuma despesa</td></tr>
                ) : despesas.filter(d => {
                  if (!filterSituacao) return true;
                  if (filterSituacao === 'VENCIDA') {
                    const hoje = new Date().toISOString().split('T')[0];
                    return (d.situacao === 'ABERTA' || !d.situacao) && d.data_vencimento && d.data_vencimento < hoje;
                  }
                  if (filterSituacao === 'HOJE') {
                    const hoje = new Date().toISOString().split('T')[0];
                    return (d.situacao === 'ABERTA' || !d.situacao) && d.data_vencimento === hoje;
                  }
                  return d.situacao === filterSituacao || (!d.situacao && filterSituacao === 'ABERTA');
                }).map((item, i) => {
                  const hoje = new Date().toISOString().split('T')[0];
                  const vencida = item.data_vencimento && item.data_vencimento < hoje && item.situacao !== 'PAGA';
                  const venceHoje = item.data_vencimento === hoje && item.situacao !== 'PAGA';
                  
                  return (
                  <tr key={item.id} className={`border-b ${vencida ? 'bg-red-50' : venceHoje ? 'bg-amber-50' : i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                    <td className="p-2 align-middle">
                      <div className="flex items-center gap-2">
                        {vencida && <AlertTriangle className="w-4 h-4 text-red-500" />}
                        {venceHoje && <Clock className="w-4 h-4 text-amber-500" />}
                        <span className={vencida ? 'text-red-600 font-semibold' : venceHoje ? 'text-amber-600 font-semibold' : ''}>
                          {formatDate(item.data_vencimento) || formatDate(item.data)}
                        </span>
                      </div>
                    </td>
                    <td className="p-2 align-middle font-medium">
                      {item.descricao}
                      {item.recorrente && <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Recorrente</span>}
                    </td>
                    <td className="p-2 align-middle"><span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full">{getCatLabel(item.categoria, 'despesa')}</span></td>
                    <td className="p-2 align-middle">
                      <button 
                        onClick={() => atualizarSituacaoDespesa(item.id, item.situacao === 'PAGA' ? 'ABERTA' : 'PAGA')}
                        className={`text-xs px-3 py-1.5 rounded-full font-semibold cursor-pointer transition-colors ${
                          item.situacao === 'PAGA' 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                        }`}
                      >
                        {item.situacao === 'PAGA' ? (
                          <><CheckCircle2 className="w-3 h-3 inline mr-1" />Paga</>
                        ) : (
                          <><Clock className="w-3 h-3 inline mr-1" />Aberta</>
                        )}
                      </button>
                    </td>
                    <td className="p-2 align-middle">{item.filial || '-'}</td>
                    <td className="p-2 align-middle text-red-600 font-bold">{formatCurrency(item.valor)}</td>
                    <td className="p-2 align-middle text-right">
                      <Button onClick={() => openEditModal(item, 'despesa')} variant="ghost" size="sm"><Edit2 className="w-4 h-4" /></Button>
                      <Button onClick={() => { setSelectedItem(item); setModalType('despesa'); setDeleteModalOpen(true); }} variant="ghost" size="sm" className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="projecao" className="mt-4">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-slate-800 flex items-center gap-2"><Calendar className="w-5 h-5" />Projeção Financeira - Próximos 12 Meses</CardTitle></CardHeader>
            <CardContent>
              <div className="h-80 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={projecao?.projecao || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="mes_nome" stroke="#64748B" fontSize={11} />
                    <YAxis stroke="#64748B" fontSize={11} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line type="monotone" dataKey="receita" name="Receita" stroke="#059669" strokeWidth={2} />
                    <Line type="monotone" dataKey="despesa" name="Despesa" stroke="#DC2626" strokeWidth={2} />
                    <Line type="monotone" dataKey="saldo" name="Saldo" stroke="#1E293B" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-800 hover:bg-slate-800">
                    <TableHead className="text-white font-bold">Mês</TableHead>
                    <TableHead className="text-white font-bold text-right">Receita</TableHead>
                    <TableHead className="text-white font-bold text-right">Despesa</TableHead>
                    <TableHead className="text-white font-bold text-right">Saldo</TableHead>
                    <TableHead className="text-white font-bold text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(projecao?.projecao || []).map((item, i) => (
                    <TableRow key={item.mes} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <TableCell className="font-medium">{item.mes_nome}</TableCell>
                      <TableCell className="text-emerald-600 font-medium text-right">{formatCurrency(item.receita)}</TableCell>
                      <TableCell className="text-red-600 font-medium text-right">{formatCurrency(item.despesa)}</TableCell>
                      <TableCell className={`font-bold text-right ${item.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(item.saldo)}</TableCell>
                      <TableCell className="text-center"><span className={`text-xs px-2 py-1 rounded-full ${item.real ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>{item.real ? 'Real' : 'Projetado'}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Criar/Editar Receita/Despesa */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${modalType === 'receita' ? 'bg-emerald-500' : 'bg-red-500'}`}>
                {modalType === 'receita' ? <TrendingUp className="w-4 h-4 text-white" /> : <TrendingDown className="w-4 h-4 text-white" />}
              </div>
              {selectedItem ? 'Editar' : 'Nova'} {modalType === 'receita' ? 'Receita' : 'Despesa'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Descrição *</Label><Input value={formData.descricao} onChange={(e) => setFormData({...formData, descricao: e.target.value})} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Valor (R$) *</Label><Input type="text" inputMode="decimal" placeholder="Ex: 1.500,00" value={formData.valor} onChange={handleValorChange} required /></div>
              <div><Label>{modalType === 'despesa' ? 'Data Lançamento' : 'Data'} *</Label><Input type="date" value={formData.data} onChange={(e) => setFormData({...formData, data: e.target.value})} required /></div>
            </div>
            
            {/* Campos extras para Despesas */}
            {modalType === 'despesa' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data Vencimento</Label>
                  <Input type="date" value={formData.data_vencimento} onChange={(e) => setFormData({...formData, data_vencimento: e.target.value})} />
                </div>
                <div>
                  <Label>Situação</Label>
                  <Select value={formData.situacao} onValueChange={(v) => setFormData({...formData, situacao: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[100]">
                      <SelectItem value="ABERTA">
                        <span className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-amber-500" /> Aberta
                        </span>
                      </SelectItem>
                      <SelectItem value="PAGA">
                        <span className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" /> Paga
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Tipo *</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white z-[100]"><SelectItem value="FIXA">Fixa</SelectItem><SelectItem value="VARIAVEL">Variável</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoria *</Label>
                <div className="flex gap-2">
                  <Select value={formData.categoria} onValueChange={(v) => setFormData({...formData, categoria: v})}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent className="bg-white z-[100]">
                      {currentCategorias.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={() => setCategoriaModalOpen(true)}
                    className="shrink-0 border-slate-300 hover:bg-slate-100"
                    title="Criar nova categoria"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            <div><Label>Filial</Label>
              <Select value={formData.filial} onValueChange={(v) => setFormData({...formData, filial: v})}>
                <SelectTrigger><SelectValue placeholder="Selecione (opcional)" /></SelectTrigger>
                <SelectContent className="bg-white z-[100]">{filiais.map(f => <SelectItem key={f.id} value={f.codigo}>{f.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <input type="checkbox" id="recorrente" checked={formData.recorrente} onChange={(e) => setFormData({...formData, recorrente: e.target.checked})} className="w-5 h-5" />
              <Label htmlFor="recorrente">Lançamento recorrente (mensal)<span className="block text-xs text-slate-400">Usado na projeção de 12 meses</span></Label>
            </div>
            <div><Label>Observações</Label><textarea value={formData.observacoes} onChange={(e) => setFormData({...formData, observacoes: e.target.value})} className="w-full h-20 px-3 py-2 rounded-md border border-slate-300 resize-none" /></div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" onClick={() => setModalOpen(false)} variant="outline">Cancelar</Button>
              <Button type="submit" className={modalType === 'receita' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-500 hover:bg-red-600'}>Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Criar Nova Categoria */}
      <Dialog open={categoriaModalOpen} onOpenChange={setCategoriaModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${modalType === 'receita' ? 'bg-emerald-500' : 'bg-red-500'}`}>
                <Tag className="w-4 h-4 text-white" />
              </div>
              Gerenciar Categorias de {modalType === 'receita' ? 'Receita' : 'Despesa'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Criar nova categoria */}
            <div>
              <Label className="text-slate-700 font-medium">Criar Nova Categoria</Label>
              <div className="flex gap-2 mt-2">
                <Input 
                  value={novaCategoria} 
                  onChange={(e) => setNovaCategoria(e.target.value)} 
                  placeholder="Ex: Consultoria, Frete, etc."
                  className="flex-1"
                />
                <Button 
                  onClick={handleCreateCategoria} 
                  className={modalType === 'receita' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-500 hover:bg-red-600'}
                >
                  <Plus className="w-4 h-4 mr-1" /> Criar
                </Button>
              </div>
            </div>

            {/* Lista de categorias customizadas */}
            {currentCategorias.filter(c => c.custom).length > 0 && (
              <div>
                <Label className="text-slate-700 font-medium">Categorias Personalizadas</Label>
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {currentCategorias.filter(c => c.custom).map(cat => (
                    <div key={cat.value} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <span className="text-slate-700">{cat.label}</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteCategoria(cat.value)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Info sobre categorias padrão */}
            <p className="text-xs text-slate-400 italic">
              As categorias padrão do sistema não podem ser excluídas.
            </p>
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button type="button" onClick={() => { setCategoriaModalOpen(false); setNovaCategoria(''); }} variant="outline">Fechar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Excluir */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-800">Confirmar Exclusão</DialogTitle></DialogHeader>
          <p className="text-slate-600 py-4">Excluir {modalType === 'receita' ? 'receita' : 'despesa'}: <strong>{selectedItem?.descricao}</strong>?</p>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button onClick={() => setDeleteModalOpen(false)} variant="outline">Cancelar</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Configurar Alertas WhatsApp */}
      <Dialog open={configAlertasOpen} onOpenChange={setConfigAlertasOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-600" />
              Alertas de Despesas via WhatsApp
            </DialogTitle>
          </DialogHeader>
          
          {configAlertas && (
            <div className="space-y-4">
              {/* Ativar/Desativar */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-semibold text-slate-700">Alertas Automáticos</p>
                  <p className="text-xs text-slate-500">Receba avisos de despesas vencidas</p>
                </div>
                <button
                  type="button"
                  onClick={() => setConfigAlertas({...configAlertas, ativo: !configAlertas.ativo})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${configAlertas.ativo ? 'bg-blue-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${configAlertas.ativo ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              
              {/* Telefones de destino */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Smartphone className="w-4 h-4" /> Telefones para receber alertas
                </Label>
                <div className="space-y-2">
                  {(configAlertas.telefones_destino || []).map((tel, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
                      <span className="flex-1 text-sm">{tel}</span>
                      <button type="button" onClick={() => removerTelefone(idx)} className="text-red-500 hover:text-red-700">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={adicionarTelefone} className="w-full">
                    <Plus className="w-4 h-4 mr-1" /> Adicionar Telefone
                  </Button>
                </div>
              </div>
              
              {/* Horários */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Horário Inicial</Label>
                  <Input 
                    type="time" 
                    value={configAlertas.horario_inicial || '08:00'}
                    onChange={(e) => setConfigAlertas({...configAlertas, horario_inicial: e.target.value})}
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Repetir a cada</Label>
                  <Select 
                    value={String(configAlertas.intervalo_horas || 4)}
                    onValueChange={(v) => setConfigAlertas({...configAlertas, intervalo_horas: parseInt(v)})}
                  >
                    <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white z-[100]">
                      <SelectItem value="1">1 hora</SelectItem>
                      <SelectItem value="2">2 horas</SelectItem>
                      <SelectItem value="4">4 horas</SelectItem>
                      <SelectItem value="6">6 horas</SelectItem>
                      <SelectItem value="12">12 horas</SelectItem>
                      <SelectItem value="24">24 horas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Até às</Label>
                  <Input 
                    type="time" 
                    value={configAlertas.horario_limite || '20:00'}
                    onChange={(e) => setConfigAlertas({...configAlertas, horario_limite: e.target.value})}
                    className="text-sm"
                  />
                </div>
              </div>
              
              {/* Tipos de alerta */}
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Enviar alertas para:</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={configAlertas.enviar_vencendo_hoje !== false}
                      onChange={(e) => setConfigAlertas({...configAlertas, enviar_vencendo_hoje: e.target.checked})}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm">Vencendo hoje</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={configAlertas.enviar_vencidas !== false}
                      onChange={(e) => setConfigAlertas({...configAlertas, enviar_vencidas: e.target.checked})}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm">Vencidas</span>
                  </label>
                </div>
              </div>
              
              {/* Botões */}
              <div className="flex justify-between pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={enviarAlertaTeste}
                  disabled={enviandoTeste || !(configAlertas.telefones_destino?.length > 0)}
                  className="border-blue-300 text-blue-600"
                >
                  {enviandoTeste ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Enviar Teste
                </Button>
                <div className="flex gap-2">
                  <Button type="button" onClick={() => setConfigAlertasOpen(false)} variant="outline">Cancelar</Button>
                  <Button type="button" onClick={salvarConfigAlertas} className="bg-blue-600 hover:bg-blue-700">Salvar</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
